import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map } from "rxjs";
import { ApiService } from "./api-service";
import { environment } from "src/environments/environment";
import { degrees, PDFDocument, rgb, StandardFonts } from "pdf-lib";

@Injectable({
  providedIn: "root",
})
export class EvaluatorService {
  constructor(private api: ApiService, private httpClient: HttpClient) {}

  getApplications() {
    const url = `/evaluator/applications`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getApplicationFiles(applicationId) {
    const url = `/evaluator/application/${applicationId}/labresults`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getXrayTypes() {
    const url = `/XRayDiagnosis`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  addXrayDiagnosis(body) {
    const url = "/XRayDiagnosis";
    return this.api.post(url, body);
  }

  getApplicationSupportingFiles(applicationId) {
    const url = `/evaluator/application/${applicationId}/supporting-files`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolClassifications() {
    const url = `/stool-examination/classifications`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolColors() {
    const url = `/stool-examination/colors`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolConsistencies() {
    const url = `/stool-examination/consistencies`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolMicroscope() {
    const url = `/stool-examination/microscopic-examinations`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolExamRemarks() {
    const url = `/stool-examination/exam-remarks`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolMethods() {
    const url = `/stool-examination/methods`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getStoolExamination(applicationId) {
    const url = `/stool-examination?applicationId=${applicationId}`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getLabHistory(applicationId) {
    const url = `/applications/${applicationId}/labresults/history`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getRemarks(applicationId) {
    const url = `/remarks/${applicationId}`;
    return this.api.get(url).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  addRemark(body) {
    const url = `/remark`;
    return this.api.post(url, body);
  }

  stoolExamination(body, applicationId) {
    const url = `/stool-examination?applicationId=${applicationId}`;
    return this.api.post(url, body);
  }

  evaluatorRegister(body) {
    const url = "/evaluator/Register";
    return this.api.post(url, body);
  }

  evaluatorLogin(body) {
    const url = "/evaluator/Login";
    return this.api.post(url, body);
  }

  evaluatorVerify(body) {
    const url = "/evaluator/verify-email";
    return this.api.post(url, body);
  }

  fileReview(body, applicationId, fileId) {
    const url = `/evaluator/application/${applicationId}/labresult/${fileId}/status`;
    return this.api.put(url, body);
  }

  updateApplicationStatus(body, applicationId) {
    const url = `/application/${applicationId}/status`;
    return this.api.patch(url, body);
  }

  uploadSupportFile(body) {
    const url = `${environment.apiUrl}/evaluator/supporting-files`;
    return this.api.postFile(url, body);
  }

  evaluatorUpdateStatus(
    id: number | string,
    body: IEvaluatorUpdateStatusPayload
  ) {
    const url = `/evaluator/update-status/${id}`;
    return this.api.put(url, body);
  }

  updateAttendance(body) {
    const url = `${environment.apiUrl}/Scheduler/updateAttendance`;
    // return this.api.postUrl(url, body, {});
    return this.httpClient.post(url, body, {});
  }

  generateSignature(userId: number, body) {
    const url = `/evaluator/generateSignature/${userId}`;
    return this.api.post(url, body);
  }

  cmoApproveAll() {
    const url = `/evaluator/cmo/approve-all`;
    return this.api.post(url, {});
  }

  async insertSignature(doc_path: string, eSig_path: string) {
    const url = doc_path;
    const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());

    const pdfDocLoad = await PDFDocument.load(existingPdfBytes, {
      parseSpeed: Infinity,
    });

    const compliantImgBytes = await fetch(eSig_path).then((res) =>
      res.arrayBuffer()
    );

    const compliantImg = await pdfDocLoad.embedPng(compliantImgBytes);

    const pages = pdfDocLoad.getPages();

    const pageCount = pages.length;
    const { width, height } = pages[0].getSize();
    var pngDims;

    for (let i = 0; i < pageCount; i++) {
      pngDims = compliantImg.scaleToFit(width, height);
      pages[i].drawImage(compliantImg, {
        x: 340,
        y: 80,
        width: pngDims.width * 0.35,
        height: pngDims.height * 0.35,
      });
    }

    const pdfBytes = await pdfDocLoad.save({
      objectsPerTick: Infinity,
    });

    const blob = new Blob([pdfBytes], { type: "application/pdf" });

    return blob;
  }
}

export interface IEvaluatorUpdateStatusPayload {
  evalStatus: number;
  reasonForChange: string;
  nonCompliantType: number;
}

export interface IUpdateAttendancePayload {
  applicantId: string;
  timeslotId: string;
  isAttended: boolean;
}
